/// <reference path="../types/env.d.ts" />
import { z } from 'zod';

const envSchema = z.object({
  VITE_API_BASE_URL: z.string().url(),
  VITE_APP_TITLE: z.string().min(1),
  VITE_ENABLE_MOCK_API: z.string().transform((v) => v === 'true'),
  VITE_MSAL_CLIENT_ID: z.string().uuid(),
  VITE_MSAL_TENANT_ID: z.string().uuid(),
  VITE_API_SCOPE: z.string().min(1).optional(),
  VITE_AZURE_AD_AUTHORITY: z.string().url(),
  VITE_AZURE_AD_CLIENT_ID: z.string().uuid(),

  VITE_APPROVER_USER_GUIDE_URL: z.string().min(1).optional(),
  VITE_ADMIN_USER_GUIDE_URL: z.string().min(1).optional(),
  VITE_LOG_ISSUE_URL: z.string().min(1).optional(),
  VITE_ONETRUST_SDK_URL: z.string().min(1).optional(),
  VITE_ONETRUST_DOMAIN_SCRIPT: z.string().min(1).optional(),

  // Feature flags — default to enabled; set to 'false' to disable
  VITE_NLQ_CHAT_ENABLED: z.string().default('true').transform((v) => v !== 'false'),
  VITE_IDLE_TIMEOUT_MINUTES: z.coerce.number().positive().optional(),

  VITE_TABLEAU_MEETING_DETAILS_URL: z.string().url().optional(),
  // Template URL — {0} is replaced with the meeting ID at runtime.
  // Must NOT use z.string().url() because {0} makes the string an invalid URL.
  VITE_SUMMIT_MEETING_REQUEST_URL: z.string().min(1).optional(),
  /** Meeting request form URL — opened in a new tab when sign-off is in progress (mirrors Blazor OnClickRequestMeeting) */
  VITE_MEETING_REQUEST_URL: z.string().url().optional(),
  VITE_COS_DASHBOARD_URL: z.string().url().optional(),
  VITE_CKEDITOR_LICENSE_KEY: z.string().min(1).optional(),
});

type RuntimeConfig = Partial<Record<keyof z.input<typeof envSchema>, string>>;

function isUnresolvedToken(value: string): boolean {
  return /^__[^\s]+__$/.test(value);
}

function getRuntimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined' || !(window.__Mable_CONFIG__)) {
    return {};
  }

  const filteredEntries = Object.entries((window.__Mable_CONFIG__)!).filter(([, value]) => {
    if (typeof value !== 'string') {
      return false; 
    }

    return !isUnresolvedToken(value);
  });

  return Object.fromEntries(filteredEntries) as RuntimeConfig;
}

export const env = envSchema.parse({
  ...import.meta.env,
  ...getRuntimeConfig(),
});

/** Feature flags derived from runtime / build-time config. */
export const featureFlags = {
  /** Mirror of FeatureFlags:NLQChatEnabled in appsettings — hides NLQ chat when false. */
  nlqChatEnabled: env.VITE_NLQ_CHAT_ENABLED,
} as const;

export function getSummitMeetingUrl(meetingId: string): string {
  if (!meetingId) return '';
  // Prefer the runtime-configured URL; the template uses {0} as the meeting-ID placeholder
  const template = env.VITE_SUMMIT_MEETING_REQUEST_URL ?? 'https://summit.deloitte.com/meeting-management/{0}';
  return template.replace('{0}', encodeURIComponent(meetingId));
}
